/**
 * Household detail — tabs: settlements, members, categories, settings.
 *
 * This file is the slim orchestrator: it loads the household, renders
 * the header + tabs strip, and dispatches to one of four tab components
 * under `./household/tabs/`. Each tab manages its own data fetches,
 * mutations, and dialogs.
 */

import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";

import { t } from "../i18n";
import { formatRelative } from "../lib/format";
import { useFavoriteHouseholds } from "../lib/settings";
import { trpc } from "../lib/trpc";
import { HouseholdBreadcrumb } from "./HouseholdBreadcrumb";
import { CategoriesTab } from "./household/tabs/CategoriesTab";
import { MembersTab } from "./household/tabs/MembersTab";
import { SettingsTab } from "./household/tabs/SettingsTab";
import { SettlementsTab } from "./household/tabs/SettlementsTab";
import {
  Badge,
  EmptyState,
  PageHeader,
  Skeleton,
  SkeletonBreadcrumb,
  SkeletonGate,
  type TabItem,
  Tabs,
  useToast,
} from "./ui";

export const HOUSEHOLD_TABS = ["settlements", "members", "categories", "settings"] as const;
export type HouseholdTab = (typeof HOUSEHOLD_TABS)[number];

export interface HouseholdDetailProps {
  householdId: string;
  tab: HouseholdTab;
}

export default function HouseholdDetail({ householdId, tab }: HouseholdDetailProps) {
  // Realtime subscription lives in `<AppShell>` via `useUserConnection`
  // — one WebSocket per tab covers every household this user belongs to,
  // so we don't need a per-page hook here anymore.

  const navigate = useNavigate();
  const onTabChange = (nextTab: HouseholdTab) =>
    navigate({
      to: "/households/$householdId/$tab",
      params: { householdId, tab: nextTab },
    });
  const onSelectSettlement = (settlementId: string) =>
    navigate({
      to: "/households/$householdId/settlements/$settlementId",
      params: { householdId, settlementId },
    });

  const household = trpc.household.get.useQuery({ groupId: householdId });
  const { show } = useToast();
  const { isFavorite } = useFavoriteHouseholds();
  const favorited = isFavorite(householdId);
  const accessDeniedCode = household.isError ? household.error.data?.code : null;
  useEffect(() => {
    if (accessDeniedCode !== "FORBIDDEN" && accessDeniedCode !== "NOT_FOUND") return;
    show({
      tone: "warning",
      title: t.household.accessRevokedToast,
      dismissLabel: t.invitations.toast.dismissLabel,
    });
    navigate({ to: "/" });
  }, [accessDeniedCode, navigate, show]);

  const tabs: TabItem<HouseholdTab>[] = [
    { value: "settlements", label: t.household.tabs.settlements, icon: "receipt" },
    { value: "members", label: t.household.tabs.members, icon: "users" },
    { value: "categories", label: t.household.tabs.categories, icon: "tag" },
    { value: "settings", label: t.household.tabs.settings, icon: "settings" },
  ];

  if (household.isPending) {
    return (
      <>
        <PageHeader
          eyebrow={<SkeletonBreadcrumb />}
          title={<Skeleton className="h-6" style={{ width: "14rem" }} />}
          description={<Skeleton className="mt-1 h-4" style={{ width: "18rem" }} />}
        />
        <Tabs items={tabs} value={tab} onChange={onTabChange} />
        <div className="mt-6">
          <SkeletonGate loading={true} shape="list" rows={4} label={t.common.loading}>
            {null}
          </SkeletonGate>
        </div>
      </>
    );
  }
  if (household.isError || !household.data) {
    return (
      <EmptyState
        icon="alert-circle"
        title={t.household.loadFailed}
        description={household.error?.message}
      />
    );
  }

  const data = household.data;

  return (
    <>
      <PageHeader
        eyebrow={
          <HouseholdBreadcrumb currentHouseholdId={householdId} currentHouseholdName={data.name} />
        }
        title={data.name}
        description={
          <span className="flex flex-col gap-1.5">
            <span>{t.households.createdAt(formatRelative(data.createdAt))}</span>
            <span className="inline-flex flex-wrap items-center gap-1.5">
              {favorited && (
                <Badge
                  tone="warning"
                  iconLeft="star"
                  className="border border-amber-400 dark:border-amber-500 [&_svg]:fill-amber-500 dark:[&_svg]:fill-amber-400"
                >
                  {t.households.favoriteBadge}
                </Badge>
              )}
              {data.role === "owner" ? (
                <Badge tone="accent" variant="solid" iconLeft="crown">
                  {t.roles.owner}
                </Badge>
              ) : (
                <Badge tone="neutral" variant="outline" iconLeft="user">
                  {t.roles.member}
                </Badge>
              )}
            </span>
          </span>
        }
      />

      <div className="mb-6">
        <Tabs
          items={tabs.map((item) =>
            item.value === "members" ? { ...item, count: data.members.length } : item,
          )}
          value={tab}
          onChange={onTabChange}
        />
      </div>

      {tab === "settlements" && (
        <SettlementsTab householdId={householdId} onSelect={onSelectSettlement} />
      )}
      {tab === "members" && (
        <MembersTab
          householdId={householdId}
          members={data.members}
          pendingInvitations={data.pendingInvitations}
          revokedNotifications={data.revokedNotifications}
          role={data.role}
        />
      )}
      {tab === "categories" && (
        <CategoriesTab householdId={householdId} categories={data.categories} />
      )}
      {tab === "settings" && (
        <SettingsTab
          householdId={householdId}
          name={data.name}
          role={data.role}
          ownerCoversInvoice={data.ownerCoversInvoice}
        />
      )}
    </>
  );
}
